import React, { useCallback, useMemo, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import flow from 'lodash/fp/flow';

import { AgGridReact } from 'ag-grid-react';
import {
  ModuleRegistry,
  ColumnHoverModule,
  TooltipModule,
  CellStyleModule,
  LocaleModule,
  RowStyleModule,
  ExternalFilterModule,
  ColumnAutoSizeModule,
  RenderApiModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  ClientSideRowModelModule,
  type GridApi,
  type GridReadyEvent,
  type FirstDataRenderedEvent,
  type ProcessCellForExportParams,
  type FilterChangedEvent,
  ScrollApiModule,
  RowApiModule,
} from 'ag-grid-community';
import { ExcelExportModule } from 'ag-grid-enterprise';
import Box from '@mui/material/Box';

import useAgGridProps from '@/components/AgGrid/Companies/useAgGridProps';
import useAgGridTheme from '@/components/AgGrid/hooks/useAgGridTheme';
import useAgGridRtlMethods from '@/components/AgGrid/hooks/useAgGridRtlMethods';
import useAgGridTranslations from '@/components/AgGrid/hooks/useAgGridTranslations';
import { CellSelectionModule, GroupFilterModule, MultiFilterModule, SetFilterModule } from 'ag-grid-enterprise';
import { addFilters } from '@/components/AgGrid/helpers/filterHelpers';
import { filterMapping } from '@/components/AgGrid/Companies/filterHelpers';
import {
  addColDefaultParams,
  GRID_FILTER_BUTTONS,
  setExelModeFilter,
  suppressSelectAll,
  addHeaderTooltipPropsForColDefs,
} from '@/components/AgGrid/helpers';
import AgGridColumnHeader from '@/components/AgGrid/components/ColumnHeader';
import AgDateFilter from '@/components/AgGrid/components/AgDateFilter/AgDateFilterFE';
import useAgGridColumnTypes from '@/components/AgGrid/Companies/useAgGridColumnTypes';
import useFetchGridData from '@/pages/companies/components/GridView/useFetchGridData';
import CircularProgressWithBackdrop from '@/components/mui/CircularProgressWithBackdrop';
import { addLinkForNameCell } from '@/pages/companies/components/GridView/helpers';

// inject order important for styles
import AgGridGlobalStyles from '@/components/AgGrid/components/AgGridGlobalStyles';
import AgGridStyles from '@/components/AgGrid/Companies/AgGridStyles';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ColumnHoverModule,
  LocaleModule,
  RowStyleModule,
  ExternalFilterModule,
  TooltipModule,
  CellStyleModule,
  ColumnAutoSizeModule,
  RenderApiModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  CustomFilterModule,
  CellSelectionModule,
  ExcelExportModule,
  ScrollApiModule,
  RowApiModule,
]);

interface GridViewProps {
  onRowCountChange: (count: number | null) => void;
}

interface GridViewHandle {
  exportToExcel: () => void;
}

const GridView = forwardRef<GridViewHandle, GridViewProps>(({ onRowCountChange }, ref) => {
  const agGridDefaultProps = useAgGridProps();
  const agGridTheme = useAgGridTheme();
  const { invertPinnedWhenRTL, enableRtl } = useAgGridRtlMethods();
  const { getLocaleText } = useAgGridTranslations();
  const columnTypes = useAgGridColumnTypes();
  const gridApiRef = useRef<GridApi | null>(null);

  const onGridReady = useCallback((e: GridReadyEvent) => {
    gridApiRef.current = e.api;
  }, []);

  const { rawColumnDefs, rowData, rawDataIsBusy } = useFetchGridData();

  useEffect(() => {
    if (!gridApiRef?.current) return;

    const count = gridApiRef.current.getDisplayedRowCount();

    onRowCountChange(count);
  }, [rowData, onRowCountChange]);

  const handleFilterChanged = useCallback(
    (params: FilterChangedEvent) => {
      const count = params.api.getDisplayedRowCount();

      onRowCountChange(count);
    },
    [onRowCountChange],
  );

  const handleFirstDataRendered = useCallback(
    (params: FirstDataRenderedEvent) => {
      const count = params.api.getDisplayedRowCount();
      const visibleColumns = params.api.getDisplayedCenterColumns();
      const gridWidth = params.api.getHorizontalPixelRange().right;
      const totalWidth = visibleColumns.reduce((sum, col) => sum + col.getActualWidth(), 0);
      onRowCountChange(count);

      // Switch strategy if there's free space
      if (totalWidth < gridWidth - 20) {
        params.api.sizeColumnsToFit();
      } else {
        params.api.autoSizeAllColumns();
      }
    },
    [onRowCountChange],
  );

  const defaultColumnDefs = useMemo(() => {
    if (rawDataIsBusy) return [];

    return flow([
      addFilters(filterMapping),
      addColDefaultParams,
      setExelModeFilter('windows', [GRID_FILTER_BUTTONS.APPLY, GRID_FILTER_BUTTONS.RESET]),
    ])(rawColumnDefs);
  }, [rawColumnDefs, rawDataIsBusy]);

  const columnDefs = useMemo(
    () =>
      flow(
        addHeaderTooltipPropsForColDefs,
        suppressSelectAll,
        invertPinnedWhenRTL,
        addLinkForNameCell,
      )(defaultColumnDefs),
    [defaultColumnDefs, invertPinnedWhenRTL],
  );

  const frameworkComponents = useMemo(
    () => ({
      agColumnHeader: AgGridColumnHeader,
      customDateFilter: AgDateFilter,
    }),
    [],
  );

  const excelStyles = useMemo(
    () => [
      {
        id: 'header',
        font: {
          bold: true,
        },
      },
    ],
    [],
  );

  useEffect(() => {
    return () => onRowCountChange(null);
  }, [onRowCountChange]);

  useImperativeHandle(
    ref,
    () => ({
      exportToExcel() {
        gridApiRef.current?.exportDataAsExcel({
          fileName: 'organization_report-' + new Date().toISOString() + '.xlsx',
          processCellCallback: (params: ProcessCellForExportParams) => {
            if (params.column.colDef.type === 'boolean') {
              return params.value ? 'yes' : 'no';
            }
            if (params.column.colDef.type === 'date') {
              return columnTypes.date?.valueFormatter?.(params as any) ?? params.value;
            }
            return params.value;
          },
        });
      },
    }),
    [columnTypes],
  );

  if (rawDataIsBusy) return <CircularProgressWithBackdrop type="light" />;

  return (
    <Box className="ag-theme-dokka" display="flex" flexGrow={1} flexDirection="column" height="100%">
      <AgGridGlobalStyles />
      <AgGridStyles />
      <AgGridReact
        {...agGridDefaultProps}
        onGridReady={onGridReady}
        theme={agGridTheme}
        rowData={rowData}
        columnDefs={columnDefs}
        enableRtl={enableRtl}
        getLocaleText={getLocaleText}
        components={frameworkComponents}
        columnTypes={columnTypes}
        onFirstDataRendered={handleFirstDataRendered}
        onFilterChanged={handleFilterChanged}
        excelStyles={excelStyles}
      />
    </Box>
  );
});

GridView.displayName = 'GridView';

export default GridView;
